import { Fragment, useEffect, useState } from 'react';
import {
  CalendarIcon,
  ChevronDownIcon,
  EllipsisIcon,
  MailIcon,
  PlusIcon,
  RefreshCcwIcon,
  SearchIcon,
  Settings2Icon,
  ShieldAlertIcon,
  ShieldCheckIcon,
  Trash2Icon,
  UserCheckIcon,
  UserMinusIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  banUser,
  changeUserRole,
  listUsers,
  removeUser,
  type AdminUserRecord,
  authClient,
  unbanUser,
  listRegions,
  sendActivationEmailAsAdmin,
  getBrowserUrl,
  updateUserAsAdmin,
} from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { useNavigate } from 'react-router-dom';
import { 
  ToolbarButton, 
  TruncatedValue, 
  SortHeaderButton, 
  type SortDirection 
} from '@/components/ui/table-shared';
import { 
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { UsersTableColumnsPopover, type UsersTableColumnOption } from '@/components/users-table-columns-popover';
import { ConfirmationModal } from './confirmation-modal';

type SessionData = NonNullable<ReturnType<typeof authClient.useSession>['data']>;
type SessionUserWithRole = SessionData['user'] & { role?: string | null };
type SortKey = 'createdAt' | 'deactivatedAt' | 'email' | 'emailVerified' | 'name' | 'regions' | 'role' | 'status' | 'updatedAt';
type RoleFilter = 'all' | 'admin' | 'user';
type StatusFilter = 'all' | 'active' | 'disabled';
type UsersQueryResponse = NonNullable<Awaited<ReturnType<typeof listUsers>>['data']>;
type UserTableColumnKey = 'createdAt' | 'deactivatedAt' | 'email' | 'name' | 'role' | 'status' | 'regions';

type UsersManagementPageProps = {
  session: SessionData;
};

type SortPreset = `${SortKey}:${SortDirection}`;

const roleFilterOptions: Array<{ label: string; value: RoleFilter }> = [
  { label: 'Tous les rôles', value: 'all' },
  { label: 'Administrateurs', value: 'admin' },
  { label: 'Membres', value: 'user' },
];

const statusFilterOptions: Array<{ label: string; value: StatusFilter }> = [
  { label: 'Tous les statuts', value: 'all' },
  { label: 'Actifs', value: 'active' },
  { label: 'Désactivés', value: 'disabled' },
];

const pageSizeOptions = [20, 50, 100] as const;

const sortPresetOptions: Array<{ label: string; value: SortPreset }> = [
  { label: 'Ordre personnalisé', value: 'createdAt:desc' },
  { label: 'Nom (A-Z)', value: 'name:asc' },
  { label: 'Nom (Z-A)', value: 'name:desc' },
  { label: 'Dernière mise à jour', value: 'updatedAt:desc' },
  { label: 'Vérifiés d’abord', value: 'emailVerified:desc' },
  { label: 'Actifs d’abord', value: 'status:asc' },
];

const USERS_TABLE_COLUMNS_STORAGE_KEY = 'vibecode-admin-users-table-columns';

const defaultTableColumns: Array<UsersTableColumnOption & { key: UserTableColumnKey | 'regions' }> = [
  { key: 'name', label: 'Nom', visible: true },
  { key: 'email', label: 'Email', visible: true },
  { key: 'role', label: 'Rôle', visible: true },
  { key: 'status', label: 'Statut', visible: true },
  { key: 'regions', label: 'Région assignée', visible: true },
  { key: 'createdAt', label: 'Créé le', visible: true },
  { key: 'deactivatedAt', label: 'Désactivé le', visible: true },
];

function readStoredTableColumns(): typeof defaultTableColumns {
  if (typeof window === 'undefined') {
    return defaultTableColumns;
  }

  try {
    const raw = window.localStorage.getItem(USERS_TABLE_COLUMNS_STORAGE_KEY);

    if (!raw) {
      return defaultTableColumns;
    }

    const parsed = JSON.parse(raw) as Array<Partial<UsersTableColumnOption> & { key?: string }>;

    if (!Array.isArray(parsed)) {
      return defaultTableColumns;
    }

    const defaultColumnsMap = new Map(defaultTableColumns.map((column) => [column.key, column]));
    const normalizedColumns = parsed
      .map((column) => {
        if (!column.key) {
          return null;
        }

        const defaultColumn = defaultColumnsMap.get(column.key as UserTableColumnKey);

        if (!defaultColumn) {
          return null;
        }

        return {
          ...defaultColumn,
          visible: typeof column.visible === 'boolean' ? column.visible : defaultColumn.visible,
        };
      })
      .filter((column): column is (typeof defaultTableColumns)[number] => column !== null);

    const missingColumns = defaultTableColumns.filter(
      (defaultColumn) => !normalizedColumns.some((column) => column.key === defaultColumn.key),
    );

    const mergedColumns = [...normalizedColumns, ...missingColumns];
    const visibleCount = mergedColumns.filter((column) => column.visible).length;

    if (visibleCount === 0) {
      return defaultTableColumns;
    }

    return mergedColumns;
  } catch {
    return defaultTableColumns;
  }
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
}

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function emptyUsersResponse(pageSize: number): UsersQueryResponse {
  return {
    items: [],
    meta: {
      filteredTotal: 0,
      page: 1,
      pageCount: 1,
      pageSize,
      total: 0,
    },
    summary: {
      activeUsers: 0,
      adminUsers: 0,
      disabledUsers: 0,
      pendingVerificationUsers: 0,
      totalUsers: 0,
      verifiedUsers: 0,
    },
  };
}

function getSortPresetLabel(sortKey: SortKey, sortDirection: SortDirection) {
  const preset = `${sortKey}:${sortDirection}` as SortPreset;
  return sortPresetOptions.find((option) => option.value === preset)?.label ?? 'Ordre personnalisé';
}

function getApiStatusFilter(statusFilter: StatusFilter) {
  if (statusFilter === 'active') {
    return 'Actif';
  }

  if (statusFilter === 'disabled') {
    return 'Inactif';
  }

  return 'all';
}

export function UsersManagementPage({ session }: UsersManagementPageProps) {
  const [usersResponse, setUsersResponse] = useState<UsersQueryResponse>(() => emptyUsersResponse(20));
  const [isLoading, setIsLoading] = useState(true);
  const [showLoadingSkeleton, setShowLoadingSkeleton] = useState(false);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [tableColumns, setTableColumns] = useState<typeof defaultTableColumns>(() => readStoredTableColumns());
  const [searchValue, setSearchValue] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [regions, setRegions] = useState<Array<{ id: string; name: string }>>([]);
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof pageSizeOptions)[number]>(20);
  const debouncedSearchValue = useDebouncedValue(searchValue, 280);
  const navigate = useNavigate();
  const currentUserId = session.user.id;

  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant: 'default' | 'destructive';
  }>({
    isOpen: false,
    title: '',
    description: '',
    onConfirm: () => {},
    variant: 'default',
  });


  useEffect(() => {
    async function fetchAllRegions() {
      const res = await listRegions();
      if (res.data?.data) {
        setRegions(
          res.data.data
            .filter((region) => region.status === 'Active')
            .map((region) => ({ id: region.id, name: region.name })),
        );
      }
    }
    void fetchAllRegions();
  }, []);

  async function fetchUsers() {
    const result = await listUsers({
      page,
      pageSize,
      regionId: regionFilter !== 'all' ? regionFilter : undefined,
      role: roleFilter,
      search: debouncedSearchValue || undefined,
      sortBy: sortKey,
      sortDirection,
      status: getApiStatusFilter(statusFilter),
    });

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return null;
    }

    setUsersResponse(result.data ?? emptyUsersResponse(pageSize));
    setIsLoading(false);

    return result.data ?? emptyUsersResponse(pageSize);
  }

  useEffect(() => {
    let isCancelled = false;

    async function loadUsers() {
      const result = await listUsers({
        page,
        pageSize,
        regionId: regionFilter !== 'all' ? regionFilter : undefined,
        role: roleFilter,
        search: debouncedSearchValue || undefined,
        sortBy: sortKey,
        sortDirection,
        status: getApiStatusFilter(statusFilter),
      });

      if (isCancelled) {
        return;
      }

      if (result.error) {
        toast.error(getAuthErrorMessage(result.error));
        setUsersResponse(emptyUsersResponse(pageSize));
        setIsLoading(false);
        return;
      }

      setUsersResponse(result.data ?? emptyUsersResponse(pageSize));
      setIsLoading(false);
    }

    void loadUsers();

    return () => {
      isCancelled = true;
    };
  }, [debouncedSearchValue, page, pageSize, regionFilter, roleFilter, sortDirection, sortKey, statusFilter]);

  useEffect(() => {
    if (!isLoading) return;

    const timeoutId = window.setTimeout(() => {
      setShowLoadingSkeleton(true);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isLoading]);

  const users = usersResponse.items;
  const { meta, summary } = usersResponse;
  const currentRangeStart = meta.filteredTotal === 0 ? 0 : (meta.page - 1) * meta.pageSize + 1;
  const currentRangeEnd = Math.min(meta.page * meta.pageSize, meta.filteredTotal);

  function clearRoleFilter() {
    setRoleFilter('all');
    setPage(1);
  }

  function clearStatusFilter() {
    setStatusFilter('all');
    setPage(1);
  }

  function clearRegionFilter() {
    setRegionFilter('all');
    setPage(1);
  }

  const activeFilters = [
    roleFilter !== 'all'
      ? {
          key: 'role',
          label: roleFilterOptions.find((option) => option.value === roleFilter)?.label ?? roleFilter,
          onClear: clearRoleFilter,
        }
      : null,
    statusFilter !== 'all'
      ? {
          key: 'status',
          label: statusFilterOptions.find((option) => option.value === statusFilter)?.label ?? statusFilter,
          onClear: clearStatusFilter,
        }
      : null,
    regionFilter !== 'all'
      ? {
          key: 'region',
          label: regions.find((region) => region.id === regionFilter)?.name ?? 'Région filtrée',
          onClear: clearRegionFilter,
        }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; onClear: () => void }>;

  async function handleDisableUser(user: AdminUserRecord) {
    setConfirmState({
      isOpen: true,
      title: 'Désactiver le membre ?',
      description: `Voulez-vous vraiment désactiver le membre ${user.email} ? Toutes ses sessions seront révoquées immédiatement.`,
      variant: 'destructive',
      onConfirm: async () => {
        const result = await banUser(user.id, 'Compte désactivé par un administrateur.');
        if (result.error) {
          toast.error(getAuthErrorMessage(result.error));
          return;
        }
        const statusResult = await updateUserAsAdmin(user.id, { status: 'Inactif' });
        if (statusResult.error) {
          toast.error(getAuthErrorMessage(statusResult.error));
          return;
        }
        toast.success('Membre désactivé.');
        await fetchUsers();
      },
    });
  }

  async function handleEnableUser(user: AdminUserRecord) {
    const result = await unbanUser(user.id);

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    const statusResult = await updateUserAsAdmin(user.id, { status: 'Actif' });

    if (statusResult.error) {
      toast.error(getAuthErrorMessage(statusResult.error));
      return;
    }

    toast.success('Membre réactivé.');
    await fetchUsers();
  }

  async function handleDeleteUser(user: AdminUserRecord) {
    setConfirmState({
      isOpen: true,
      title: 'Supprimer le membre ?',
      description: `Voulez-vous vraiment supprimer définitivement ${user.email} ? Cette action est irréversible.`,
      variant: 'destructive',
      onConfirm: async () => {
        const result = await removeUser(user.id);
        if (result.error) {
          toast.error(getAuthErrorMessage(result.error));
          return;
        }
        toast.success('Membre supprimé.');
        if (users.length === 1 && page > 1) {
          setPage((current) => current - 1);
          return;
        }
        await fetchUsers();
      },
    });
  }

  async function handleResendVerificationEmail(user: AdminUserRecord) {
    const result = await sendActivationEmailAsAdmin({
      callbackURL: getBrowserUrl(`/auth/set-password?email=${encodeURIComponent(user.email)}`),
      email: user.email,
    });

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    toast.success('Email d’activation renvoyé.');
  }

  async function handleToggleRole(user: AdminUserRecord) {
    if (user.id === currentUserId) {
      toast.error('Tu ne peux pas modifier ton propre rôle depuis ce tableau.');
      return;
    }

    const nextRole = user.role === 'admin' ? 'user' : 'admin';
    const title = nextRole === 'admin' ? 'Promouvoir admin ?' : 'Rétrograder membre ?';
    const description = nextRole === 'admin'
      ? `Promouvoir ${user.email} en tant qu’administrateur ?`
      : `Rétrograder ${user.email} en tant que membre standard ?`;

    setConfirmState({
      isOpen: true,
      title,
      description,
      variant: 'default',
      onConfirm: async () => {
        const result = await changeUserRole({
          role: nextRole,
          userId: user.id,
        });

        if (result.error) {
          toast.error(getAuthErrorMessage(result.error));
          return;
        }

        toast.success(nextRole === 'admin' ? 'Membre promu admin.' : 'Membre repassé en rôle standard.');
        await fetchUsers();
      },
    });
  }

  function handleSearchChange(value: string) {
    setSearchValue(value);
    setPage(1);
  }

  function handlePageSizeChange(value: (typeof pageSizeOptions)[number]) {
    setPageSize(value);
    setPage(1);
  }

  function handleSortPresetChange(value: string) {
    const [nextSortKey, nextSortDirection] = value.split(':') as [SortKey, SortDirection];
    setSortKey(nextSortKey);
    setSortDirection(nextSortDirection);
    setPage(1);
  }

  function toggleSort(nextKey: SortKey) {
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      setPage(1);
      return;
    }

    setSortKey(nextKey);
    setSortDirection(nextKey === 'name' ? 'asc' : 'desc');
    setPage(1);
  }

  const currentSortPreset = `${sortKey}:${sortDirection}` as SortPreset;
  const sortPresetLabel = getSortPresetLabel(sortKey, sortDirection);
  const visibleTableColumns = tableColumns.filter((column) => column.visible) as Array<UsersTableColumnOption & { key: UserTableColumnKey }>;

  useEffect(() => {
    window.localStorage.setItem(USERS_TABLE_COLUMNS_STORAGE_KEY, JSON.stringify(tableColumns));
  }, [tableColumns]);

  function resetTableColumns() {
    setTableColumns(defaultTableColumns);
  }

  function renderHeaderCell(column: (typeof defaultTableColumns)[number]) {
    switch (column.key) {
      case 'name':
        return (
          <SortHeaderButton active={sortKey === 'name'} direction={sortDirection} onClick={() => toggleSort('name')}>
            {column.label}
          </SortHeaderButton>
        );
      case 'email':
        return (
          <SortHeaderButton active={sortKey === 'email'} direction={sortDirection} onClick={() => toggleSort('email')}>
            {column.label}
          </SortHeaderButton>
        );
      case 'role':
        return (
          <SortHeaderButton active={sortKey === 'role'} direction={sortDirection} onClick={() => toggleSort('role')}>
            {column.label}
          </SortHeaderButton>
        );
      case 'status':
        return (
          <SortHeaderButton active={sortKey === 'status'} direction={sortDirection} onClick={() => toggleSort('status')}>
            {column.label}
          </SortHeaderButton>
        );
      case 'createdAt':
        return (
          <SortHeaderButton active={sortKey === 'createdAt'} direction={sortDirection} onClick={() => toggleSort('createdAt')}>
            {column.label}
          </SortHeaderButton>
        );
      case 'deactivatedAt':
        return (
          <SortHeaderButton active={sortKey === 'deactivatedAt'} direction={sortDirection} onClick={() => toggleSort('deactivatedAt')}>
            {column.label}
          </SortHeaderButton>
        );
      case 'regions':
        return (
          <SortHeaderButton active={sortKey === 'regions'} direction={sortDirection} onClick={() => toggleSort('regions')}>
            {column.label}
          </SortHeaderButton>
        );
    }
  }

  function renderSkeletonCell(columnKey: UserTableColumnKey) {
    switch (columnKey) {
      case 'name':
        return (
          <div className="flex items-center gap-4">
            <Skeleton className="size-16 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-48 rounded-full" />
              <Skeleton className="h-4 w-24 rounded-full" />
            </div>
          </div>
        );
      case 'email':
        return <Skeleton className="h-5 w-56 rounded-full" />;
      case 'role':
        return <Skeleton className="h-8 w-28 rounded-full" />;
      case 'status':
        return <Skeleton className="h-8 w-24 rounded-full" />;
      case 'createdAt':
        return <Skeleton className="h-5 w-36 rounded-full" />;
      case 'deactivatedAt':
        return <Skeleton className="h-5 w-40 rounded-full" />;
      case 'regions':
        return <Skeleton className="h-5 w-36 rounded-full" />;
    }
  }

  function renderUserCell(columnKey: UserTableColumnKey, user: AdminUserRecord, isCurrentUser: boolean) {
    const displayStatus = !user.emailVerified ? 'En attente' : user.banned || user.status === 'Inactif' ? 'Inactif' : 'Actif';

    switch (columnKey) {
      case 'name':
        return (
          <div className="flex min-h-16 items-center gap-4">
            <Avatar size="lg" className="size-16 ring-2 ring-border ring-offset-2 ring-offset-background">
              <AvatarImage src={user.image ?? undefined} alt={user.name} />
              <AvatarFallback className="bg-muted font-semibold text-foreground">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>

            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <TruncatedValue className="max-w-[18rem] text-[17px] font-semibold text-foreground">
                  {user.name}
                </TruncatedValue>
                {isCurrentUser ? (
                  <span className="rounded-full bg-foreground px-2.5 py-1 text-xs font-medium text-background">
                    Vous
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        );
      case 'email':
        return (
          <div className="flex min-h-16 items-center">
            <div className="inline-flex min-w-0 items-center gap-2 text-[15px] text-muted-foreground">
              <MailIcon className="-translate-y-px size-4 shrink-0 text-muted-foreground" />
              <TruncatedValue className="max-w-[14rem] leading-none">{user.email}</TruncatedValue>
            </div>
          </div>
        );
      case 'role':
        return (
          <div className="flex min-h-16 items-center">
            <span className={cn(
              'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-medium',
              user.role === 'admin' ? 'bg-secondary text-secondary-foreground' : 'bg-muted text-muted-foreground',
            )}
            >
              {user.role === 'admin' ? <ShieldCheckIcon className="size-3.5" /> : <UsersIcon className="size-3.5" />}
              {user.role === 'admin' ? 'Admin' : 'Membre'}
            </span>
          </div>
        );
      case 'status':
        return (
          <div className="flex min-h-16 items-center">
            <span className={cn(
              'inline-flex rounded-full px-2.5 py-1 text-sm font-medium',
              displayStatus === 'Inactif'
                ? 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300'
                : displayStatus === 'En attente'
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
            )}
            >
              {displayStatus}
            </span>
          </div>
        );
      case 'createdAt':
        return (
          <div className="flex min-h-16 items-center">
            <div className="inline-flex min-w-0 items-center gap-2 text-[15px] text-muted-foreground">
              <CalendarIcon className="-translate-y-px size-4 shrink-0 text-muted-foreground" />
              <TruncatedValue className="max-w-[8rem] leading-none">{formatDate(user.createdAt)}</TruncatedValue>
            </div>
          </div>
        );
      case 'deactivatedAt':
        return (
          <div className="flex min-h-16 items-center">
            <div className="inline-flex min-w-0 items-center gap-2 text-[15px] text-muted-foreground">
              <CalendarIcon className="-translate-y-px size-4 shrink-0 text-muted-foreground" />
              <TruncatedValue className="max-w-[10rem] leading-none">{user.deactivatedAt ? formatDate(user.deactivatedAt) : '-'}</TruncatedValue>
            </div>
          </div>
        );
      case 'regions':
        return (
          <div className="flex min-h-16 items-center">
            <div className="flex flex-wrap gap-1 max-w-[15rem]">
              {user.regions && user.regions.length > 0 ? (
                <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground ring-1 ring-inset ring-border">
                  {user.regions[0]?.name}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground italic">Aucune</span>
              )}
            </div>
          </div>
        );
    }
  }

  function getPaginationItems() {
    if (meta.pageCount <= 1) {
      return [1];
    }

    const pages = new Set<number>([1, meta.page, meta.pageCount]);

    if (meta.page > 2) {
      pages.add(meta.page - 1);
    }

    if (meta.page < meta.pageCount - 1) {
      pages.add(meta.page + 1);
    }

    return Array.from(pages).sort((left, right) => left - right);
  }

  const paginationItems = getPaginationItems();
  const skeletonRowCount = Math.min(pageSize, 4);

  return (
    <section className="flex min-h-[calc(100svh-10rem)] flex-col gap-6 text-foreground">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">Membres de l’équipe</h1>
            <span className="inline-flex min-w-8 items-center justify-center rounded-full border border-border bg-card px-2.5 py-1 text-sm font-medium text-muted-foreground">
              {meta.filteredTotal}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Gestion des comptes, invitations et rôles depuis une vue unique.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => navigate('/admin/users/new')}
            variant="outline"
            data-testid="users-add-button"
            className="h-12 border-black bg-black px-6 text-[15px] font-medium text-white shadow-none transition-colors hover:border-black/85 hover:bg-black/85 hover:text-white dark:border-border dark:bg-muted dark:text-foreground dark:hover:border-foreground/20 dark:hover:bg-muted/80 dark:hover:text-foreground"
          >
            <PlusIcon />
            Ajouter un membre
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[var(--radius-surface-4xl)] border border-border/70 bg-card">
        <div className="border-b border-border/70 bg-[#f4f4f538] p-4 sm:p-5 dark:bg-muted/40">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex w-full max-w-[42rem] items-center gap-2">
              <div className="relative min-w-0 flex-1 max-w-[34rem]">
                <SearchIcon className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="users-search-input"
                  value={searchValue}
                  onChange={(event) => handleSearchChange(event.target.value)}
                  placeholder="Rechercher les membres de l’équipe"
                  className="h-12 border-border bg-background pl-11 pr-11 text-[15px] shadow-none placeholder:text-muted-foreground dark:bg-muted"
                />
                {searchValue ? (
                  <button
                    type="button"
                    aria-label="Effacer la recherche"
                    onClick={() => handleSearchChange('')}
                    className="absolute top-1/2 right-3 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <XIcon className="size-4" />
                  </button>
                ) : null}
              </div>

              <ToolbarButton onClick={() => setIsFilterSheetOpen(true)} className="shrink-0 gap-2 px-4">
                Filtres
                <Settings2Icon className="size-4" />
              </ToolbarButton>
            </div>

            <div className="flex items-center justify-end gap-2 xl:ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <ToolbarButton className="justify-between pr-6 xl:min-w-56">
                    {sortPresetLabel}
                    <ChevronDownIcon className="size-4" />
                  </ToolbarButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Ordre d’affichage</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuRadioGroup value={currentSortPreset} onValueChange={handleSortPresetChange}>
                    {sortPresetOptions.map((option) => (
                      <DropdownMenuRadioItem key={option.value} value={option.value}>
                        {option.label}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>

              <UsersTableColumnsPopover
                columns={tableColumns}
                onChange={(columns) => setTableColumns(columns as typeof defaultTableColumns)}
                onReset={resetTableColumns}
              />
            </div>
          </div>

          {activeFilters.length > 0 ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {activeFilters.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={filter.onClear}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:border-foreground/20 hover:bg-muted dark:bg-muted dark:hover:bg-muted/80"
                >
                  <span>{filter.label}</span>
                  <XIcon className="size-3.5 text-muted-foreground" />
                </button>
              ))}

              {activeFilters.length >= 3 ? (
                <button
                  type="button"
                  onClick={() => {
                    clearRoleFilter();
                    clearStatusFilter();
                    clearRegionFilter();
                  }}
                  className="text-sm font-medium text-black underline underline-offset-4 transition-colors hover:text-black/80 dark:text-foreground dark:hover:text-foreground/80"
                >
                  Effacer tout
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          <table className="min-w-full border-separate border-spacing-0">
            <thead>
              <tr className="text-left text-sm text-muted-foreground">
                {visibleTableColumns.map((column) => (
                  <th key={column.key} className="px-6 py-5">
                    {renderHeaderCell(column)}
                  </th>
                ))}
                <th className="px-6 py-5 text-right text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>

            <tbody>
              {isLoading && showLoadingSkeleton
                ? Array.from({ length: skeletonRowCount }).map((_, index) => (
                    <tr key={`skeleton-${index}`}>
                      {visibleTableColumns.map((column) => (
                        <td key={column.key} className="border-t border-border px-6 py-5 align-middle">
                          {renderSkeletonCell(column.key)}
                        </td>
                      ))}
                      <td className="border-t border-border px-6 py-5 align-middle">
                        <Skeleton className="ml-auto h-10 w-28 rounded-full" />
                      </td>
                    </tr>
                  ))
                : users.map((user) => {
                    const isCurrentUser = user.id === currentUserId;

                    return (
                      <tr
                        key={user.id}
                        data-testid={`user-row-${user.id}`}
                        className="group cursor-pointer align-middle transition-colors hover:bg-accent/10"
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                      >
                        {visibleTableColumns.map((column) => (
                          <td key={column.key} className="border-t border-border px-6 py-5 align-middle transition-colors group-hover:bg-accent/10">
                            {renderUserCell(column.key, user, isCurrentUser)}
                          </td>
                        ))}

                        <td className="border-t border-border px-6 py-5 align-middle transition-colors group-hover:bg-accent/10">
                          <div className="flex items-center justify-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon-sm"
                                  className="h-10 w-10 border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted dark:bg-muted dark:hover:bg-muted/80"
                                  data-testid={`user-actions-${user.id}`}
                                  aria-label={`Actions pour ${user.email}`}
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <EllipsisIcon className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-60">
                                <DropdownMenuLabel>Actions membre</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem disabled={isCurrentUser} onClick={(event) => {
                                  event.stopPropagation();
                                  void handleToggleRole(user);
                                }}>
                                  {user.role === 'admin' ? <ShieldAlertIcon /> : <ShieldCheckIcon />}
                                  {user.role === 'admin' ? 'Rétrograder en membre' : 'Promouvoir admin'}
                                </DropdownMenuItem>
                                {user.banned ? (
                                  <DropdownMenuItem onClick={(event) => {
                                    event.stopPropagation();
                                    void handleEnableUser(user);
                                  }}>
                                    <UserCheckIcon />
                                    Réactiver le compte
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem disabled={isCurrentUser} onClick={(event) => {
                                    event.stopPropagation();
                                    void handleDisableUser(user);
                                  }}>
                                    <UserMinusIcon />
                                    Désactiver le compte
                                  </DropdownMenuItem>
                                )}
                                {!user.emailVerified ? (
                                  <DropdownMenuItem onClick={(event) => {
                                    event.stopPropagation();
                                    void handleResendVerificationEmail(user);
                                  }}>
                                    <RefreshCcwIcon />
                                    Renvoyer l’email
                                  </DropdownMenuItem>
                                ) : null}
                                <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/admin/users/${user.id}`);
                        }}>
                          <Settings2Icon />
                          Gérer / Modifier
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600 focus:bg-red-50 focus:text-red-600 dark:focus:bg-red-500/10"
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleDeleteUser(user);
                          }}
                        >
                          <Trash2Icon />
                          Supprimer
                        </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>

        {!isLoading && users.length === 0 ? (
          <div className="flex flex-col items-center gap-3 border-t border-border px-6 py-16 text-center">
            <div className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <UsersIcon className="size-6" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-foreground">Aucun membre ne correspond à cette vue</h3>
              <p className="max-w-md text-sm leading-6 text-muted-foreground">
                Ajuste la recherche, les filtres ou l’ordre d’affichage pour retrouver rapidement le bon compte.
              </p>
            </div>
          </div>
        ) : null}

        <div id="users-pagination" className="grid gap-3 border-t border-border px-6 py-4 md:grid-cols-[1fr_auto_1fr] md:items-center">
          <p className="text-sm text-muted-foreground">
            {currentRangeStart}-{currentRangeEnd} sur {meta.filteredTotal} membres affichés ({summary.totalUsers} au total)
          </p>

          <Pagination className="justify-center">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#users-pagination"
                  aria-disabled={meta.page <= 1}
                  tabIndex={meta.page <= 1 ? -1 : 0}
                  className={cn(meta.page <= 1 ? 'pointer-events-none opacity-50' : undefined)}
                  onClick={(event) => {
                    event.preventDefault();
                    if (meta.page <= 1) {
                      return;
                    }
                    setPage((current) => current - 1);
                  }}
                />
              </PaginationItem>

              {paginationItems.map((pageNumber, index) => {
                const previousPage = paginationItems[index - 1];
                const shouldRenderEllipsis = previousPage && pageNumber - previousPage > 1;

                return (
                  <Fragment key={pageNumber}>
                    {shouldRenderEllipsis ? (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : null}
                    <PaginationItem>
                      <PaginationLink
                        href="#users-pagination"
                        isActive={pageNumber === meta.page}
                        onClick={(event) => {
                          event.preventDefault();
                          setPage(pageNumber);
                        }}
                        aria-label={`Aller à la page ${pageNumber}`}
                      >
                        {pageNumber}
                      </PaginationLink>
                    </PaginationItem>
                  </Fragment>
                );
              })}

              <PaginationItem>
                <PaginationNext
                  href="#users-pagination"
                  aria-disabled={meta.page >= meta.pageCount}
                  tabIndex={meta.page >= meta.pageCount ? -1 : 0}
                  className={cn(meta.page >= meta.pageCount ? 'pointer-events-none opacity-50' : undefined)}
                  onClick={(event) => {
                    event.preventDefault();
                    if (meta.page >= meta.pageCount) {
                      return;
                    }
                    setPage((current) => current + 1);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>

          <div className="flex justify-start md:justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ToolbarButton className="h-10 justify-between pl-4 pr-5 text-sm">
                  {pageSize} lignes
                  <ChevronDownIcon className="size-4" />
                </ToolbarButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Lignes par page</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup value={String(pageSize)} onValueChange={(value) => handlePageSizeChange(Number(value) as (typeof pageSizeOptions)[number])}>
                  {pageSizeOptions.map((option) => (
                    <DropdownMenuRadioItem key={option} value={String(option)}>
                      {option} lignes
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
        <SheetContent side="right" className="w-full border-border sm:max-w-md">
          <SheetHeader className="border-b border-border">
            <SheetTitle>Filtres</SheetTitle>
            <SheetDescription>
              Ajuste le rôle, le statut et la région sans quitter la table.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-8 overflow-y-auto px-6 py-6">
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Rôle</h3>
              <div className="flex flex-wrap gap-2">
                {roleFilterOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setRoleFilter(option.value);
                      setPage(1);
                    }}
                    className={cn(
                      'rounded-full border px-4 py-2 text-sm transition-colors',
                      roleFilter === option.value
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Statut</h3>
              <div className="flex flex-wrap gap-2">
                {statusFilterOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setStatusFilter(option.value);
                      setPage(1);
                    }}
                    className={cn(
                      'rounded-full border px-4 py-2 text-sm transition-colors',
                      statusFilter === option.value
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Région</h3>
              <div className="flex flex-wrap gap-2">
                {[{ id: 'all', name: 'Toutes les régions' }, ...regions].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      setRegionFilter(option.id);
                      setPage(1);
                    }}
                    className={cn(
                      'rounded-full border px-4 py-2 text-sm transition-colors',
                      regionFilter === option.id
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                    )}
                  >
                    {option.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <SheetFooter className="border-t border-border sm:flex-row sm:justify-between">
            <Button variant="outline" onClick={() => {
              clearRoleFilter();
              clearStatusFilter();
              clearRegionFilter();
            }}
            >
              Réinitialiser
            </Button>
            <Button onClick={() => setIsFilterSheetOpen(false)}>
              Appliquer
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        onClose={() => setConfirmState(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        description={confirmState.description}
        variant={confirmState.variant}
      />
    </section>
  );
}
export function AdminUsersPage({ session }: { session: SessionData }) {
  if ((((session?.user as SessionUserWithRole | undefined)?.role) ?? 'user').toLowerCase() !== 'admin') {
    return (
      <section className="rounded-[var(--radius-surface-4xl)] border border-border bg-card p-8 shadow-[0_8px_24px_rgba(15,23,42,0.03)]">
        <div className="max-w-xl space-y-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium tracking-[0.18em] text-amber-700 uppercase">
            <ShieldCheckIcon className="size-3.5" />
            Accès restreint
          </div>
          <h1 className="text-2xl font-semibold text-foreground">Zone réservée aux administrateurs</h1>
          <p className="text-sm leading-6 text-muted-foreground">
            La gestion des utilisateurs est limitée aux comptes admin. Le système de sécurité appliquera de toute façon
            la même contrainte côté API.
          </p>
        </div>
      </section>
    );
  }

  return <UsersManagementPage session={session} />;
}
